// @flow
import React, { PureComponent, Fragment } from 'react';

import EventListPagination from 'components/event/EventListPagination';
import EventListNavbar from 'components/event/EventListNavbar';
import EventListRow from 'components/event/EventListRow';
import Breadcrumb from 'components/fragments/Breadcrumb';
import Loading from 'components/fragments/Loading';
import FooterArticle from 'components/newsletter/FooterArticle';
import { STATUS_SUCCESS, STATUS_LOADING } from 'constants/statusConstants';
import { SORT_DATE, SORT_NAME } from 'constants/sortConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import { getPublicAssets } from 'utils/assetsUtils';
import type { ArticleType } from 'types/Article';
import type { EventType } from 'types/Event';
import type { Status } from 'types/Status';
import { groupBy } from 'utils/arrayUtils';
import { getMonthNumber, getMonthName } from 'utils/dateUtils';
import { sortEventsByTitleAndDate, getEventsLabels } from 'utils/eventUtils';
import { getMetaBySlug } from 'utils/seoUtils';

export type StateProps = {
  events: Array<EventType>,
  status: Status,
  articles: Array<ArticleType>
};

export type DispatchProps = {
  fetchEvents: (from: Date, to: Date) => void,
  fetchLigueArticles: () => void
};

type Props = {
  match: {
    params: {
      month?: string,
      year?: string
    }
  },
  history: any
};

type allProps = Props & StateProps & DispatchProps;

type State = {
  currentMonth: Date,
  label: ?string,
  sort: SORT_DATE | SORT_NAME
};

class EventList extends PureComponent<allProps, State> {
  props: allProps;
  state: State = {
    currentMonth: new Date(),
    label: null,
    sort: SORT_DATE
  };

  componentDidMount() {
    const { month, year } = this.props.match.params;
    if (month && year) {
      let curMonth;
      curMonth = decodeURI(month);
      curMonth = getMonthNumber(curMonth);
      curMonth = new Date(`${year}-${curMonth < 10 ? `0${curMonth}` : curMonth}-01`);

      if (isNaN(curMonth.getTime())) {
        curMonth = new Date();
      }
      this.setState({
        currentMonth: curMonth
      });
      this.updateEvents(curMonth);
    } else {
      this.updateEvents(this.state.currentMonth);
    }
    this.props.fetchLigueArticles();
  }

  updateEvents(date: Date) {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth + 2, 0);

    this.props.fetchEvents(firstDay, lastDay);
  }

  updateCurrentMonth = (e: MouseEvent, currentMonth: Date) => {
    e.preventDefault();
    this.updateEvents(currentMonth);
    this.setState({ currentMonth });
  };

  handleIncrementCurrentMonth = (e: MouseEvent) => {
    const date = this.state.currentMonth;
    date.setMonth(date.getMonth() + 1);
    const month = getMonthName(date.getMonth());
    this.props.history.push(`/evenements/${date.getFullYear()}/${encodeURIComponent(month.toLowerCase())}`);
    this.updateCurrentMonth(e, new Date(date));
  };

  handleDecrementCurrentMonth = (e: MouseEvent) => {
    const date = this.state.currentMonth;
    date.setMonth(date.getMonth() - 1);
    const month = getMonthName(date.getMonth());
    this.props.history.push(`/evenements/${date.getFullYear()}/${encodeURI(month.toLowerCase())}`);
    this.updateCurrentMonth(e, new Date(date));
  };

  handleSortChanged = (sort: SORT_DATE | SORT_NAME) => {
    this.setState({ sort });
  };

  setLabel = (label: ?string) => {
    this.setState({ label });
  };

  sortEvents = (events: Array<EventType>): any => {
    const { currentMonth, sort } = this.state;
    const eventsOfTheMonth = this.getEventsOfTheMonth(events, currentMonth);

    if (sort === SORT_DATE) {
      return this.getEventsByDateRows(eventsOfTheMonth);
    } else if (sort === SORT_NAME) {
      return sortEventsByTitleAndDate(eventsOfTheMonth, this.state.label).map((el, index) => (
        <EventListRow key={index} events={el.events} noHidden />
      ));
    } else {
      return null;
    }
  };

  getEventsByDateRows(events: Array<EventType>) {
    // on trie les events par date
    let sortedEventsByStartDate = events.sort((a, b) =>
      new Date(a.startDate) < new Date(b.startDate) ? -1 : new Date(a.startDate) > new Date(b.startDate) ? 1 : 0
    );

    // on souhaite regrouper les événements par date de début complète
    const groupedByStartDateEvents = groupBy(sortedEventsByStartDate, item => item.startDate.toString().slice(0, 10));

    // on récupère la liste des jours de départ
    const startDays = Object.keys(groupedByStartDateEvents);

    return startDays.length
      ? startDays.map(ev => {
          // si on n'a qu'un élément dans la date du jour, on rend directement l'événement dans une row
          if (groupedByStartDateEvents[ev].length === 1) {
            return <EventListRow key={ev} events={[...groupedByStartDateEvents[ev]]} />;
          }
          // sinon on va vouloir créer autant de row qu'il y a de couples date début / date de fin différents
          else {
            // on trie les events par date de fin
            const sortedEventsByEndDate = groupedByStartDateEvents[ev].sort((a, b) =>
              a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0
            );

            // on souhaite regrouper les événements par date de fin complète
            const groupedByEndDateEvents = groupBy(sortedEventsByEndDate, item => item.endDate.toString().slice(0, 10));

            // on récupère la liste des jours de fin
            const endDays = Object.keys(groupedByEndDateEvents);

            return endDays.map(index => <EventListRow key={index} events={[...groupedByEndDateEvents[index]]} />);
          }
        })
      : '';
  }

  getEventsOfTheMonth(events: EventType[], date: Date) {
    const eventsOfTheMonth = events.filter(event => {
      return (
        new Date(event.startDate).getMonth() === date.getMonth() ||
        new Date(event.endDate).getMonth() === date.getMonth()
      );
    });

    return eventsOfTheMonth;
  }

  renderEvents = () => {
    const { events } = this.props;
    const { label } = this.state;

    if (this.props.status === STATUS_LOADING) {
      return <Loading isWhite />;
    }

    if (this.props.status === STATUS_SUCCESS) {
      if (label !== null) {
        const filteredEvents = events.filter(event => event.label === label);

        return this.sortEvents(filteredEvents);
      }
      return this.sortEvents(events);
    }
    return null;
  };

  render() {
    const labels = getEventsLabels(this.props.events);
    return (
      <Fragment>
        <section className="banner banner--big">
          <div
            className="banner__thumbnail img-blue-alt"
            style={{ backgroundImage: `url(${getPublicAssets('/img/banner/gerer-mon-club.jpg')})` }}
          />
          <div className="container">
            <Breadcrumb elements={[{ name: 'accueil', to: '/' }, 'Événements']} isDark />
            <h1 className="ft-h2">Événements</h1>
            <p className="banner__desc">Retrouvez l’ensemble des événements qui se déroulent dans votre région.</p>
          </div>
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7 mb-lg-8">
            <div className="col">
              <div className="box events">
                <EventListNavbar
                  handleSortChanged={this.handleSortChanged}
                  currentMonth={this.state.currentMonth}
                  handleLinkClicked={this.setLabel}
                  labels={labels}
                />
                {this.renderEvents()}
                <EventListPagination
                  currentMonth={this.state.currentMonth}
                  handleDecrementCurrentMonth={this.handleDecrementCurrentMonth}
                  handleIncrementCurrentMonth={this.handleIncrementCurrentMonth}
                />
              </div>
            </div>
          </div>
          <FooterArticle articles={this.props.articles} slug={'ligue'} label={'Ligue'} />
        </div>
      </Fragment>
    );
  }
}

export default withHelmet(EventList)((Helmet, pageProps) => {
  const meta = getMetaBySlug('evenements');
  let { month, year } = pageProps.match.params;

  const description = meta.description(pageProps.ligue, month, year);
  const title = meta.title(pageProps.ligue, month, year);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name={'description'} content={description} />
      <link rel={'canonical'} href={window.location.href} />
      <meta property={'og:title'} content={title} />
      <meta property={'og:description'} content={description} />
      <meta property={'og:image'} content={getPublicAssets('/img/banner/gerer-mon-club.jpg')} />
    </Helmet>
  );
});
